import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ArrowRight, Sparkles } from 'lucide-react';
import { getImageUrl } from '../lib/utils-img';
import API from '../lib/api';

export default function OfferPopup({ locationId }) {
  const [offer, setOffer] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const key = locationId ? `offer_dismissed_${locationId}` : 'offer_dismissed_main';
    if (sessionStorage.getItem(key)) return;
    const endpoint = locationId ? `/offers?location_id=${locationId}` : '/offers/main';
    API.get(endpoint).then(r => {
      const o = locationId ? r.data.offers?.[0] : r.data.offer;
      if (o) setOffer(o);
    }).catch(() => {});
  }, [locationId]);

  const dismiss = () => {
    const key = locationId ? `offer_dismissed_${locationId}` : 'offer_dismissed_main';
    sessionStorage.setItem(key, '1');
    setDismissed(true);
  };

  if (!offer || dismissed) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
        onClick={dismiss}
        data-testid="offer-popup-overlay"
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 30 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 30 }}
          onClick={e => e.stopPropagation()}
          className="relative bg-[#161616] rounded-2xl overflow-hidden max-w-md w-full shadow-2xl border border-white/[0.10]"
          data-testid="offer-popup"
        >
          <button onClick={dismiss} className="absolute top-4 right-4 z-10 w-8 h-8 rounded-full bg-black/40 flex items-center justify-center text-white/60 hover:text-white transition-colors" data-testid="offer-popup-close">
            <X className="w-4 h-4" />
          </button>

          {offer.image && (
            <div className="relative h-48 overflow-hidden">
              <img src={getImageUrl(offer.image)} alt="" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#161616] to-transparent" />
            </div>
          )}

          <div className="p-6 pt-4">
            {offer.badge_text && (
              <span className="inline-flex items-center gap-1.5 bg-gold/15 text-gold text-[10px] uppercase tracking-[0.15em] font-bold px-3 py-1.5 rounded-full mb-4">
                <Sparkles className="w-3 h-3" /> {offer.badge_text}
              </span>
            )}
            <h3 className="font-heading text-2xl text-white font-bold mb-2">{offer.title}</h3>
            {offer.description && <p className="text-white/55 text-sm leading-relaxed mb-6">{offer.description}</p>}
            {offer.link_url && (
              <a href={offer.link_url} className="flex items-center justify-center gap-2 bg-gold hover:bg-gold-dark text-white text-xs uppercase tracking-[0.15em] font-semibold py-3.5 px-6 rounded-full transition-all duration-300 shadow-lg shadow-gold/20" data-testid="offer-popup-cta">
                {offer.link_text || 'Learn More'} <ArrowRight className="w-3.5 h-3.5" />
              </a>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
